import React, { useRef, useState } from "react";
import DictatedInput from "./DictatedInput.jsx";

/**
 * NoticeBar — the Note side of the person's page bar (Nov 1 redesign).
 *
 * One field, text or mic, and a Save arrow. No prompt above it and no
 * checkbox before Save: the note is saved shared with the coordinator (the
 * default since Oct 31), and the saved card says so in one line, with one tap
 * to keep it private instead. That line IS the privacy statement.
 *
 * One line under the field, always the same: incidents go through the
 * agency's process. Nothing here says or implies that notes are screened.
 *
 * Under the agency setting "immediate" only, a "Next shift too" choice sits
 * by Save (the server takes it at write time only).
 */
export const NOTICE_COPY = {
  placeholder: "What did you notice today?",
  incident: "Report incidents through the agency's process, not here.",
  shiftNow: "Next shift too",
  saved: "Saved to your notes",
  sharedLine: "Your coordinator will see this",
  privateLine: "Just for you",
  keepPrivate: "Keep it to myself",
  share: "Share with your coordinator",
};

export default function NoticeBar({ clientId, shiftSharing = "off", onSaved, onFirstUse = null, autoFocus = false }) {
  const [text, setText] = useState("");
  const [dictated, setDictated] = useState(false);
  const [shiftNow, setShiftNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);   // the note as the server returned it
  const inputRef = useRef(null);
  const immediate = shiftSharing === "immediate";

  async function save() {
    inputRef.current?.stop();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body, dictated, shared: true, to_shift_now: immediate && shiftNow }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save that.");
      setText(""); setDictated(false); setShiftNow(false);
      setSaved(d);
      onSaved?.(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function setShared(shared) {
    if (!saved) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/notes/${saved.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shared }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't change that.");
      setSaved(d);
      onSaved?.(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.wrap}>
      <div style={s.row}>
        <DictatedInput ref={inputRef} value={text}
                       onChange={(v, meta) => { setText(v); setDictated(!!meta?.dictated); setError(null); }}
                       onSubmit={save} placeholder={NOTICE_COPY.placeholder}
                       ariaLabel="What you noticed" disabled={busy} style={{ flex: 1 }}
                       clientId={clientId} onFocus={onFirstUse} autoFocus={autoFocus} />
        <button type="button" onClick={save} disabled={busy || !text.trim()} aria-label="Save note"
                style={{ ...s.send, ...(busy || !text.trim() ? s.sendOff : {}) }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </button>
      </div>
      <div style={s.under}>
        <span style={s.line}>{NOTICE_COPY.incident}</span>
        {immediate && (
          <label style={s.check}>
            <input type="checkbox" checked={shiftNow} onChange={e => setShiftNow(e.target.checked)} disabled={busy} />
            <span>{NOTICE_COPY.shiftNow}</span>
          </label>
        )}
      </div>
      {error && <p style={s.error}>{error}</p>}

      {saved && (
        <div style={s.card} role="status">
          <div style={s.cardHead}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>
            <span>{NOTICE_COPY.saved}</span>
          </div>
          <p style={s.cardText}>{saved.text}</p>
          <div style={s.cardFoot}>
            <span>{saved.shared ? NOTICE_COPY.sharedLine : NOTICE_COPY.privateLine}</span>
            {saved.editable && (
              <button type="button" style={s.link} disabled={busy} onClick={() => setShared(!saved.shared)}>
                {saved.shared ? NOTICE_COPY.keepPrivate : NOTICE_COPY.share}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:     { display: "flex", flexDirection: "column", gap: 8 },
  row:      { display: "flex", alignItems: "center", gap: 8 },
  send:     { width: 40, height: 40, borderRadius: 999, border: "none", background: "var(--color-accent)", color: "var(--color-text-on-accent)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  sendOff:  { background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)", cursor: "default" },
  under:    { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", margin: "0 0 0 4px" },
  line:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  check:    { display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", minHeight: "var(--tap-target-min)", cursor: "pointer" },
  card:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-3) var(--space-4)", display: "flex", flexDirection: "column", gap: 8, boxShadow: "0 1px 2px rgba(26,25,21,0.06)" },
  cardHead: { display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-accent)" },
  cardText: { margin: 0, fontSize: "var(--text-base)", lineHeight: 1.45, color: "var(--color-text-primary)" },
  cardFoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", borderTop: "1px solid var(--color-border)", paddingTop: 8, fontSize: "var(--text-sm)", color: "var(--color-text-primary)" },
  link:     { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-accent)", background: "none", border: "none", padding: "8px 0", cursor: "pointer" },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
};
